import { BatchSampleOutcome } from '../../api/data';
import {
  CalculateSamplePayload,
  LambdaMethod,
} from '../../@types/samples/types';
import { AnalysisMethod, SampleRunResult } from './types';
import {
  LIAO_DEFAULT_BOUNDS,
  deriveRunFailure,
  deriveRunStatus,
} from './validation';

/**
 * Build the calculate_sample payload for one sample of a batch run.
 *
 * Liao mirrors `findBestValues` in `useLiaoAnalysis` (auto-optimize with
 * default bounds + the per-sample Pce) so batch results match the
 * single-sample workflow. Brooks-Corey (lambda) mirrors the single-sample
 * refinement payload with `indexes: null` so the backend auto-selects all
 * valid regression points — the right default for a batch — and carries the
 * batch-level solver `method` (defaults to the recommended `nlsq`).
 * The Kr methodologies ('water-oil' / 'gas-oil') carry the batch run mode:
 * `auto_refine` runs the full automated refinement (true Sor, endpoint
 * extrapolation, refined exponents); `overwriteRefinement` is sent true only
 * on save after the user confirmed overwriting stored/manual refinements.
 */
export const buildBatchPayload = (
  sampleUid: string,
  method: AnalysisMethod,
  pce: number | null,
  preview = false,
  lambdaMethod: LambdaMethod = 'nlsq',
  krOptions: { autoRefine: boolean; overwriteRefinement: boolean } = {
    autoRefine: true,
    overwriteRefinement: false,
  }
): CalculateSamplePayload => {
  if (method === 'water-oil' || method === 'gas-oil') {
    return {
      sample_uid: sampleUid,
      strategies_keys: [method],
      input_data_map: {
        [method]:
          method === 'water-oil'
            ? {
                type: 'WATER_OIL',
                auto_refine: krOptions.autoRefine,
                overwrite_refinement: krOptions.overwriteRefinement,
              }
            : {
                type: 'GAS_OIL',
                auto_refine: krOptions.autoRefine,
                overwrite_refinement: krOptions.overwriteRefinement,
              },
      },
      preview,
    };
  }

  if (method === 'lambda') {
    return {
      sample_uid: sampleUid,
      strategies_keys: ['lambda'],
      input_data_map: {
        // auto_select: the backend picks the largest contiguous point
        // subset that still yields an acceptable fit, and stores the
        // chosen indexes so the single-sample editor opens with them.
        // `method` selects the Brooks-Corey solver for the whole batch.
        lambda: {
          type: 'LAMBDA',
          indexes: null,
          auto_select: true,
          method: lambdaMethod,
        },
      },
      preview,
    };
  }

  return {
    sample_uid: sampleUid,
    strategies_keys: ['liao'],
    input_data_map: {
      liao: {
        type: 'LIAO',
        bounds: LIAO_DEFAULT_BOUNDS,
        pce,
        optimize_pce: pce === null,
      },
    },
    preview,
  };
};

/** Backwards-compatible Liao-only builder (kept for existing callers/tests). */
export const buildLiaoBatchPayload = (
  sampleUid: string,
  pce: number | null,
  preview = false
): CalculateSamplePayload =>
  buildBatchPayload(sampleUid, 'liao', pce, preview);

const LAMBDA_METHOD_FALLBACK: LambdaMethod = 'nlsq';

/**
 * Pick the best Brooks-Corey solver for one sample from its per-method preview
 * results: the successful fit with the lowest Pc-space `fit_rmse`. Ties and the
 * all-failed case fall back to `nlsq` (the recommended default), or to the first
 * method actually present if nlsq wasn't run. Pure and deterministic.
 */
export const pickBestLambdaMethod = (
  perMethod: Partial<Record<LambdaMethod, SampleRunResult>>
): LambdaMethod => {
  const present = Object.keys(perMethod) as LambdaMethod[];
  let bestMethod: LambdaMethod | null = null;
  let bestRmse = Infinity;
  for (const method of present) {
    const result = perMethod[method];
    if (!result || result.status === 'failed' || !result.sample) continue;
    const rmse = result.sample.analysis_values?.lambda_values?.fit_rmse;
    if (typeof rmse !== 'number' || !Number.isFinite(rmse)) continue;
    if (rmse < bestRmse) {
      bestRmse = rmse;
      bestMethod = method;
    }
  }
  if (bestMethod) return bestMethod;
  if (present.includes(LAMBDA_METHOD_FALLBACK)) return LAMBDA_METHOD_FALLBACK;
  return present[0] ?? LAMBDA_METHOD_FALLBACK;
};

/** Convert a transport-level batch outcome into a per-sample run result. */
export const toRunResult = (
  outcome: BatchSampleOutcome,
  method: AnalysisMethod = 'liao',
  reviewed = false,
  saved = false
): SampleRunResult => {
  if (!outcome.ok || !outcome.sample) {
    return {
      sampleUid: outcome.sample_uid,
      reviewed,
      saved,
      ...deriveRunFailure(outcome.error),
    };
  }

  return {
    sampleUid: outcome.sample_uid,
    sample: outcome.sample,
    reviewed,
    saved,
    ...deriveRunStatus(outcome.sample, method),
  };
};
